import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Table,
    Sheet,
    FormControl,
    FormLabel,
    Select,
    Option,
    Textarea,
    Button,
    Divider
} from '@mui/joy';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SendIcon from '@mui/icons-material/Send';
import axiosConfig from '../util/axiosConfig';

const tableSheetSx = {
    width: '100%',
    maxWidth: '100%',
    borderRadius: 'sm',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '600px',
    WebkitOverflowScrolling: 'touch',
    border: '1px solid',
    borderColor: 'divider'
};

const messageHistoryTableSx = {
    width: 'max-content',
    minWidth: '100%',
    tableLayout: 'fixed',
    '& tbody tr:nth-of-type(even)': {
        backgroundColor: '#f9f9f9'
    },
    '& tbody tr:hover': {
        backgroundColor: '#fff5ed'
    },
    '& thead': {
        position: 'sticky',
        top: 0,
        zIndex: 10
    },
    '& thead th': {
        backgroundColor: '#eb7a24',
        color: 'white',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: '2px solid #e55125',
        whiteSpace: 'nowrap'
    },
    '& td, & th': {
        padding: '12px',
        border: '1px solid',
        borderColor: 'divider',
        verticalAlign: 'top',
        overflow: 'hidden'
    },
    '& th:nth-of-type(1), & td:nth-of-type(1)': {
        width: 130,
        minWidth: 130,
        whiteSpace: 'nowrap'
    },
    '& th:nth-of-type(2), & td:nth-of-type(2)': {
        width: 280,
        minWidth: 280,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word'
    },
    '& th:nth-of-type(3), & td:nth-of-type(3)': {
        width: 130,
        minWidth: 130,
        whiteSpace: 'nowrap'
    },
    '& th:nth-of-type(4), & td:nth-of-type(4)': {
        width: 280,
        minWidth: 280,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word'
    }
};

const messageCellTextSx = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
};

const Support = () => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [categories, setCategories] = useState([]);
    const [mgmtCategoryId, setMgmtCategoryId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [messageText, setMessageText] = useState('');
    // Examination-specific fields
    const [showExamFields, setShowExamFields] = useState(false);
    const [examRelatedTo, setExamRelatedTo] = useState('p');
    const [modules, setModules] = useState([]);
    const [selectedModule, setSelectedModule] = useState('');
    const [tests, setTests] = useState([]);
    const [selectedTest, setSelectedTest] = useState('');
    const [loadingModules, setLoadingModules] = useState(false);
    const [loadingTests, setLoadingTests] = useState(false);
    // Director appointment fields
    const [showDirectorForm, setShowDirectorForm] = useState(false);
    const [directorMessage, setDirectorMessage] = useState('');
    const [submittingDirector, setSubmittingDirector] = useState(false);

    useEffect(() => {
        fetchSupportData();
        fetchModules();
    }, []);

    useEffect(() => {
        // Check if selected category is 'examination' by name
        const selectedCategoryObj = categories.find(cat => cat.id === selectedCategoryId);
        if (selectedCategoryObj && selectedCategoryObj.name === 'examination') {
            setShowExamFields(true);
        } else {
            setShowExamFields(false);
            setSelectedModule('');
            setSelectedTest('');
        }
    }, [selectedCategoryId, categories]);

    useEffect(() => {
        if (selectedModule && selectedModule !== '') {
            fetchTests(selectedModule);
        } else {
            setTests([]);
            setSelectedTest('');
        }
    }, [selectedModule]);

    const fetchSupportData = async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/support');
            setCategories(response.data.categories || []);
            setMgmtCategoryId(response.data.mgmtCategoryId || null);
            setMessages(response.data.messages || []);
        } catch (err) {
            console.error('Error fetching support data:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load support data');
        } finally {
            setLoading(false);
        }
    };

    const fetchModules = async () => {
        try {
            setLoadingModules(true);
            const response = await axiosConfig.get('/students/support/modules');
            setModules(response.data.modules || []);
        } catch (err) {
            console.error('Error fetching modules:', err);
        } finally {
            setLoadingModules(false);
        }
    };

    const fetchTests = async (module) => {
        try {
            setLoadingTests(true);
            const response = await axiosConfig.get(`/students/support/tests?module=${encodeURIComponent(module)}`);
            // Build test options from modules (IDs) and test (names) arrays
            const testOptions = [];
            if (response.data.modules && response.data.test) {
                for (let i = 0; i < response.data.modules.length && i < response.data.test.length; i++) {
                    testOptions.push({
                        id: response.data.modules[i],
                        name: response.data.test[i]
                    });
                }
            }
            setTests(testOptions);
        } catch (err) {
            console.error('Error fetching tests:', err);
            setTests([]);
        } finally {
            setLoadingTests(false);
        }
    };

    const submitMessage = async () => {
        if (!selectedCategoryId || !messageText.trim()) {
            setError('Please select a category and enter a message');
            return;
        }

        // Get category name for examination check
        const selectedCategoryObj = categories.find(cat => cat.id === selectedCategoryId);
        const categoryName = selectedCategoryObj ? selectedCategoryObj.name : '';

        // Special handling for examination category
        let finalMessage = messageText.trim();
        if (categoryName === 'examination') {
            if (examRelatedTo === 'p') {
                // Permission: format as ~ex~p~{module}~{test}~{message}
                if (selectedModule === '' || selectedTest === '') {
                    setError('Please select the module and test for which you need permission');
                    return;
                }
                finalMessage = `~ex~p~${selectedModule}~${selectedTest}~${finalMessage}`;
            } else {
                // Others: format as {test}: {module}: {message} (if test/module are selected)
                let prefix = '';
                if (selectedTest !== '') {
                    const testName = tests.find(t => t.id === selectedTest)?.name || selectedTest;
                    prefix = `${testName}: `;
                }
                if (selectedModule !== '') {
                    prefix = `${selectedModule}: ${prefix}`;
                }
                finalMessage = prefix + finalMessage;
            }
        }

        try {
            setSubmitting(true);
            setError(null);
            setSuccess(null);

            const response = await axiosConfig.post('/students/support', {
                category: selectedCategoryId,
                message: finalMessage
            });

            setSuccess(response.data.message || 'Message submitted successfully!');
            setMessageText('');
            setSelectedCategoryId('');
            setSelectedModule('');
            setSelectedTest('');
            setExamRelatedTo('p');

            // Refresh messages
            await fetchSupportData();
        } catch (err) {
            console.error('Error submitting message:', err);
            setError(err.response?.data?.errorMessage || 'Failed to submit message');
        } finally {
            setSubmitting(false);
        }
    };

    const submitDirectorMessage = async () => {
        if (!directorMessage.trim()) {
            setError('Please enter your message to Management');
            return;
        }

        if (!mgmtCategoryId) {
            setError('Unable to submit appointment request. Please try again later.');
            return;
        }

        try {
            setSubmittingDirector(true);
            setError(null);
            setSuccess(null);

            const response = await axiosConfig.post('/students/support', {
                category: mgmtCategoryId,
                isManagement: true,
                message: directorMessage.trim()
            });

            setSuccess(response.data.message || 'Appointment request submitted successfully!');
            setDirectorMessage('');
            setShowDirectorForm(false);

            // Refresh messages
            await fetchSupportData();
        } catch (err) {
            console.error('Error submitting director message:', err);
            setError(err.response?.data?.errorMessage || 'Failed to submit appointment request');
        } finally {
            setSubmittingDirector(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading support data...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 0, m: 0, width: '100%', height: '100%', minWidth: 0 }}>
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <SupportAgentIcon sx={{ color: '#eb7a24', fontSize: '1.5rem' }} />
                        <Typography level="h3" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                            Support
                        </Typography>
                    </Box>

                    {error && (
                        <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert color="success" variant="soft" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl>
                            <FormLabel>Category</FormLabel>
                            <Select
                                value={selectedCategoryId}
                                onChange={(e, newValue) => setSelectedCategoryId(newValue || '')}
                                placeholder="Select a category"
                                sx={{
                                    '&:hover': { borderColor: '#ea7a23' },
                                    '&:focus-within': { borderColor: '#ea7a23' }
                                }}
                            >
                                {categories.map((category) => (
                                    <Option key={category.id} value={category.id}>
                                        {category.name}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>

                        {showExamFields && (
                            <>
                                <FormControl>
                                    <FormLabel>Related to</FormLabel>
                                    <Select
                                        value={examRelatedTo}
                                        onChange={(e, newValue) => setExamRelatedTo(newValue || 'p')}
                                        sx={{
                                            '&:hover': { borderColor: '#ea7a23' },
                                            '&:focus-within': { borderColor: '#ea7a23' }
                                        }}
                                    >
                                        <Option value="p">Permission</Option>
                                        <Option value="o">Others</Option>
                                    </Select>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Module</FormLabel>
                                    <Select
                                        value={selectedModule}
                                        onChange={(e, newValue) => setSelectedModule(newValue || '')}
                                        placeholder="- - Select - -"
                                        loading={loadingModules}
                                        sx={{
                                            '&:hover': { borderColor: '#ea7a23' },
                                            '&:focus-within': { borderColor: '#ea7a23' }
                                        }}
                                    >
                                        {modules.map((module) => (
                                            <Option key={module} value={module}>
                                                {module}
                                            </Option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Test</FormLabel>
                                    <Select
                                        value={selectedTest}
                                        onChange={(e, newValue) => setSelectedTest(newValue || '')}
                                        placeholder="- - Select - -"
                                        disabled={!selectedModule}
                                        loading={loadingTests}
                                        sx={{
                                            '&:hover': { borderColor: '#ea7a23' },
                                            '&:focus-within': { borderColor: '#ea7a23' }
                                        }}
                                    >
                                        {tests.map((test) => (
                                            <Option key={test.id} value={test.id}>
                                                {test.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}

                        <FormControl>
                            <FormLabel>Your Message</FormLabel>
                            <Textarea
                                value={messageText}
                                onChange={(e) => setMessageText(e.target.value)}
                                placeholder="Enter your message..."
                                minRows={4}
                                maxRows={8}
                                sx={{
                                    '&:hover': { borderColor: '#ea7a23' },
                                    '&:focus-within': { borderColor: '#ea7a23' }
                                }}
                            />
                        </FormControl>

                        <Button
                            onClick={submitMessage}
                            disabled={submitting || !selectedCategoryId || !messageText.trim()}
                            loading={submitting}
                            startDecorator={<SendIcon />}
                            sx={{
                                backgroundColor: '#eb7a24',
                                '&:hover': {
                                    backgroundColor: '#e55125'
                                },
                                maxWidth: '200px'
                            }}
                        >
                            {submitting ? 'Submitting...' : 'Send Message'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mt: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography level="body-md">
                            <Button
                                variant="plain"
                                onClick={() => setShowDirectorForm(!showDirectorForm)}
                                sx={{
                                    color: '#eb7a24',
                                    textDecoration: 'underline',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Click Here
                            </Button>
                            {' '} for appointment with Directorsir.
                        </Typography>
                    </Box>

                    {showDirectorForm && (
                        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControl>
                                <FormLabel>Reason of meeting</FormLabel>
                                <Textarea
                                    value={directorMessage}
                                    onChange={(e) => setDirectorMessage(e.target.value)}
                                    placeholder="Enter reason for meeting..."
                                    minRows={4}
                                    maxRows={8}
                                    sx={{
                                        '&:hover': { borderColor: '#ea7a23' },
                                        '&:focus-within': { borderColor: '#ea7a23' }
                                    }}
                                />
                            </FormControl>

                            <Button
                                onClick={submitDirectorMessage}
                                disabled={submittingDirector || !directorMessage.trim()}
                                loading={submittingDirector}
                                startDecorator={<SendIcon />}
                                sx={{
                                    backgroundColor: '#eb7a24',
                                    '&:hover': {
                                        backgroundColor: '#e55125'
                                    },
                                    maxWidth: '200px'
                                }}
                            >
                                {submittingDirector ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Divider sx={{ my: 3 }} />

            <Card variant="outlined">
                <CardContent sx={{ minWidth: 0 }}>
                    <Typography level="h4" sx={{ color: '#eb7a24', fontWeight: 'bold', mb: 2 }}>
                        Message History
                    </Typography>

                    {messages.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography level="body-lg" sx={{ color: '#6e7072' }}>
                                You have not yet sent any message to support
                            </Typography>
                        </Box>
                    ) : (
                        <Sheet variant="outlined" sx={tableSheetSx}>
                            <Table
                                aria-label="support messages table"
                                sx={messageHistoryTableSx}
                            >
                                <thead>
                                    <tr>
                                        <th>Your message date</th>
                                        <th>Your message</th>
                                        <th>Reply date</th>
                                        <th>Reply from support</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {messages.map((msg, index) => (
                                        <tr key={index}>
                                            <td>
                                                <Typography level="body-md" sx={{ whiteSpace: 'nowrap' }}>
                                                    {msg.date || '-'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm" sx={messageCellTextSx}>
                                                    {msg.sentMessage || '-'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-md" sx={{ whiteSpace: 'nowrap' }}>
                                                    {msg.replyDate || '-'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm" sx={messageCellTextSx}>
                                                    {msg.replyMessage || '-'}
                                                </Typography>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default Support;
